// ═══════════════════════════════════════════════════════════════════════════
// MEETING WIZARD — analyseur de compte-rendu en 2 étapes
// ═══════════════════════════════════════════════════════════════════════════
//
// Étape 1 : source du CR (texte/docx/pptx) + projet/comité de rattachement.
// Étape 2 : actions créées automatiquement (parse → IA → création), affichées
//           dans le même format que le bloc « Générer les actions » de la page
//           réunion (réutilise componentActionRow + componentCrAnalysis).
//
// La phase de validation/cancellation intermédiaire (ancien step 2) a été
// supprimée mai 2026 : toutes les actions extraites par l'IA sont créées
// automatiquement, le rattachement projet/comité est appliqué à la création.

interface MWUser {
  id: number;
  name: string;
  trigram?: string | null;
  is_team?: boolean;
}

interface MWAction {
  id: number;
  text?: string | null;
  approver_id?: number | null;
  approver_name?: string | null;
  performers?: number[];
  deadline?: string | null;
}

interface MWStatus {
  id: number;
  name: string;
  position: number;
}

interface MWProject {
  id: number;
  code?: string | null;
  name: string;
}

interface MWCommittee {
  id: number;
  code?: string | null;
  name: string;
}

declare global {
  interface Window {
    showMeetingWizard: () => Promise<void>;
  }
}

window.showMeetingWizard = async function (): Promise<void> {
  let step: 1 | 2 = 1;
  let sourceMode: 'text' | 'docx' = 'docx';
  let textInput = '';
  let docxFile: File | null = null;
  let projectId = '';
  let committeeId = '';
  let createdActions: MWAction[] = [];
  let users: MWUser[] = [];
  let statuses: MWStatus[] = [];
  let projects: MWProject[] = [];
  let committees: MWCommittee[] = [];

  try {
    [users, statuses, projects, committees] = (await Promise.all([
      loadUsers(),
      loadActionStatuses(),
      api.getProjects(),
      api.getCommittees().catch(() => []),
    ])) as unknown as [MWUser[], MWStatus[], MWProject[], MWCommittee[]];
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    toast(t('meetingWizard.toast.refsError', { msg }), 'error');
    return;
  }

  const userMap: Record<number, MWUser> = Object.fromEntries(users.map((u) => [u.id, u]));
  const projectMap: Record<number, MWProject> = Object.fromEntries(projects.map((p) => [p.id, p]));

  function projectName(id: string): string {
    const p = projects.find((x) => x.id === parseInt(id));
    return p ? `${p.code ? p.code + ' — ' : ''}${p.name}` : '';
  }

  function committeeName(id: string): string {
    const c = committees.find((x) => x.id === parseInt(id));
    return c ? c.name : '';
  }

  // ── ÉTAPE 1 — source + rattachement ─────────────────────────────────────
  function renderStep1(): string {
    const projOpts = projects
      .map(
        (p) =>
          `<option value="${p.id}"${String(p.id) === String(projectId) ? ' selected' : ''}>${esc(p.code ? p.code + ' — ' : '')}${esc(p.name)}</option>`,
      )
      .join('');
    const comOpts = committees
      .slice()
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
      .map(
        (c) =>
          `<option value="${c.id}"${String(c.id) === String(committeeId) ? ' selected' : ''}>${esc(c.name)}</option>`,
      )
      .join('');
    const docxLabel = docxFile ? `📎 ${esc(docxFile.name)}` : t('meetingWizard.step1.docxFile');
    return `
      <div style="font-size:12px;color:var(--text3);margin-bottom:12px">
        ${esc(t('meetingWizard.step1.subtitle'))}
      </div>

      <div class="card" style="margin-bottom:12px">
        <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">${esc(t('meetingWizard.step1.source'))}</div>
        <div style="display:flex;gap:16px;margin-bottom:10px">
          <label style="cursor:pointer;font-size:13px">
            <input type="radio" name="mw-source" value="docx"${sourceMode === 'docx' ? ' checked' : ''}> ${esc(t('meetingWizard.step1.sourceDocx'))}
          </label>
          <label style="cursor:pointer;font-size:13px">
            <input type="radio" name="mw-source" value="text"${sourceMode === 'text' ? ' checked' : ''}> ${esc(t('meetingWizard.step1.sourceText'))}
          </label>
        </div>

        <div id="mw-source-text" style="display:${sourceMode === 'text' ? 'block' : 'none'}">
          <textarea id="mw-text" placeholder="${esc(t('meetingWizard.step1.textPlaceholder'))}"
            style="width:100%;min-height:160px;font-size:13px;padding:8px;border:1px solid var(--border2);border-radius:var(--radius);font-family:inherit;resize:vertical">${esc(textInput)}</textarea>
        </div>

        <div id="mw-source-docx" style="display:${sourceMode === 'docx' ? 'block' : 'none'}">
          <label class="btn btn-secondary btn-sm" style="cursor:pointer">
            ${esc(docxLabel)}
            <input type="file" id="mw-docx" accept=".docx,.pptx" style="display:none">
          </label>
          <p style="font-size:11px;color:var(--text3);margin-top:6px;margin-bottom:0">
            ${esc(t('meetingWizard.step1.docxNote'))}
          </p>
        </div>
      </div>

      <div class="card" style="margin-bottom:12px">
        <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">${esc(t('meetingWizard.step1.attach'))}</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div>
            <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingWizard.step1.project'))}</label>
            <select id="mw-project" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
              <option value=""${projectId === '' ? ' selected' : ''}>${esc(t('common.none'))}</option>
              ${projOpts}
            </select>
          </div>
          <div>
            <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingWizard.step1.committee'))}</label>
            <select id="mw-committee" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
              <option value=""${committeeId === '' ? ' selected' : ''}>${esc(t('common.none'))}</option>
              ${comOpts}
            </select>
          </div>
        </div>
        <p style="font-size:11px;color:var(--text3);margin-top:8px;margin-bottom:0">
          ${esc(t('meetingWizard.step1.attachNote'))}
        </p>
      </div>
    `;
  }

  function wireStep1(): void {
    document.querySelectorAll<HTMLInputElement>('input[name="mw-source"]').forEach((r) => {
      r.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        sourceMode = (target?.value || 'docx') as 'text' | 'docx';
        const ta = document.getElementById('mw-text') as HTMLTextAreaElement | null;
        if (ta) textInput = ta.value;
        refreshBody();
      });
    });
    const ta = document.getElementById('mw-text') as HTMLTextAreaElement | null;
    if (ta)
      ta.addEventListener('input', () => {
        textInput = ta.value;
      });
    const fi = document.getElementById('mw-docx') as HTMLInputElement | null;
    if (fi)
      fi.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        docxFile = target?.files?.[0] || null;
        refreshBody();
      });
    const ps = document.getElementById('mw-project') as HTMLSelectElement | null;
    if (ps)
      ps.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLSelectElement | null;
        projectId = target?.value || '';
      });
    const cs = document.getElementById('mw-committee') as HTMLSelectElement | null;
    if (cs)
      cs.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLSelectElement | null;
        committeeId = target?.value || '';
      });
  }

  // ── ÉTAPE 1 → 2 — parse + IA + création ──────────────────────────────────
  // L'utilisateur clique « Étape 2 — Analyser ». On lance le pipeline complet
  // factorisé dans componentCrAnalysis. Chaque action créée hérite du projet
  // et du comité choisis à l'étape 1 (auto-rattachement) ainsi que de
  // l'origine « CR » pour la traçabilité.
  // Garde de réentrance : l'analyse IA (15-40 s) crée de vraies actions en
  // base. api.withButton ne protège que le clic sur le bouton confirm ; la
  // touche Entrée (onConfirm) pourrait relancer un 2e pipeline → actions
  // dupliquées (bloquant audit UX 2026-06). Ce flag couvre tous les chemins.
  let analyzing = false;

  async function gotoStep2(): Promise<void> {
    if (analyzing) return;
    let file: File | null = null;
    let text = '';
    if (sourceMode === 'docx') {
      if (!docxFile) {
        toast(t('meetingWizard.toast.docxRequired'), 'error');
        return;
      }
      file = docxFile;
    } else {
      const ta = document.getElementById('mw-text') as HTMLTextAreaElement | null;
      text = (ta?.value || textInput).trim();
      if (!text) {
        toast(t('meetingWizard.toast.textRequired'), 'error');
        return;
      }
    }

    // Validation passée : on verrouille jusqu'à la fin du pipeline (finally).
    analyzing = true;
    const overlay = window.componentCrAnalysis.showAnalyseOverlay(
      file
        ? t('pageMeeting.generation.analyse.parsing')
        : t('pageMeeting.generation.analyse.analyzing'),
      t('pageMeeting.generation.analyse.title'),
    );
    const setStep = (msg: string): void => {
      const lbl = overlay.querySelector<HTMLElement>('.cmp-cr-step');
      if (lbl) lbl.textContent = msg;
    };

    // Note de traçabilité rappelant l'origine CR + rattachement choisi à
    // l'étape 1. Variantes selon la complétude des sélections — on évite
    // les formulations bancales ("réunion  du projet ...").
    const projLabel = projectId ? projectName(projectId) : '';
    const comLabel = committeeId ? committeeName(committeeId) : '';
    const traceabilityNote = (() => {
      if (comLabel && projLabel) {
        return `Action issue du CR de la réunion ${comLabel} du projet ${projLabel}.`;
      }
      if (comLabel) return `Action issue du CR de la réunion ${comLabel}.`;
      if (projLabel) return `Action issue du CR du projet ${projLabel}.`;
      return "Action issue d'un CR analysé par l'assistant.";
    })();

    const createOne = async (action: CrProposedAction): Promise<{ id: number }> => {
      // to_validate=true → brouillon standalone. L'action reste invisible
      // des listes globales tant qu'elle n'est pas publiée via le bouton
      // « Publier » de l'étape 2. Si l'utilisateur quitte sans publier,
      // la fermeture (« Fermer ») nettoie les brouillons restants.
      // La note de traçabilité est créée par le backend dans la même
      // transaction, avec user_id=NULL (note système, pas attribuée à
      // l'utilisateur connecté). Cf. ActionUpdateIn.traceability_note.
      const payload = {
        text: action.text,
        deadline: action.echeanceIso,
        deadline_revised: null,
        status_id: null,
        progress: 0,
        approver_id: null,
        performers: action.performers,
        consulted: [],
        informed: [],
        priority: action.priority,
        project_ids: projectId ? [parseInt(projectId)] : [],
        committee_ids: committeeId ? [parseInt(committeeId)] : [],
        to_validate: true,
        traceability_note: traceabilityNote,
      };
      return (await api.createAction(payload)) as { id: number };
    };

    try {
      // Fichier ou texte collé : même pipeline (parse éventuel → IA →
      // création séquentielle). Le composant mémorise aussi la provenance
      // de chaque action créée, affichée en « ? » à l'étape 2.
      const result = await window.componentCrAnalysis.runCrAnalysis({
        ...(file ? { file } : { text }),
        callbacks: { setStep },
        createAction: async (action) => {
          const created = await createOne(action);
          if (created && created.id) createdActions.push(created as MWAction);
          return created;
        },
      });
      overlay.remove();
      if (!result.total) {
        toast(t('meetingWizard.toast.noActions'), 'info');
        return;
      }
      if (!result.done) {
        toast(t('meetingWizard.toast.noCreated'), 'error');
        return;
      }
      if (result.truncated) toast(t('meetingWizard.toast.truncated'), 'info');
      toast(t('meetingWizard.toast.created', { n: result.done }), 'ok');
    } catch (err: unknown) {
      overlay.remove();
      const msg = err instanceof Error ? err.message : String(err);
      toast(t('meetingWizard.toast.analyzeError', { msg }), 'error');
      return;
    } finally {
      analyzing = false;
    }

    if (!createdActions.length) {
      toast(t('meetingWizard.toast.noCreated'), 'error');
      return;
    }

    step = 2;
    refreshAll();
  }

  // ── ÉTAPE 2 — affichage des actions créées (bloc « Générer les actions »)
  // On réutilise componentActionRow pour avoir exactement le même rendu que
  // l'onglet « Générer les actions » d'une réunion. Chaque ligne expose les
  // 3 CTAs identiques : Publier (sort du brouillon) / ✎ Modifier / ✕ Supprimer.
  // Le contenu de createdActions reflète uniquement les BROUILLONS restants ;
  // une action publiée ou supprimée est retirée de la liste.
  function renderStep2(): string {
    const ctxParts: string[] = [];
    if (projectId)
      ctxParts.push(t('meetingWizard.step2.attachProject', { name: projectName(projectId) }));
    if (committeeId)
      ctxParts.push(t('meetingWizard.step2.attachCommittee', { name: committeeName(committeeId) }));
    const ctxLine = ctxParts.length
      ? `<div style="font-size:12px;color:var(--text3);margin-bottom:10px">${esc(t('meetingWizard.step2.attachPrefix'))}${ctxParts.join(' · ')}</div>`
      : '';

    const subtitle = `<div style="font-size:12px;color:var(--text3);margin-bottom:10px">${esc(t('meetingWizard.step2.subtitle'))}</div>`;

    if (!createdActions.length) {
      return `${subtitle}${ctxLine}<p id="mw-actions-empty" style="color:var(--text3);font-size:13px">${esc(t('meetingWizard.step2.allHandled'))}</p>`;
    }

    const opts = {
      statuses,
      userMap,
      projectMap,
      readOnly: true,
    };
    const customHeader = '<th style="width:200px"></th>';
    const headers = window.componentActionRow.buildActionHeadersHtml() + customHeader;
    // (B) Pastille « ? » au survol → extrait du CR ayant justifié l'action.
    // Magasin partagé avec l'onglet « Générer les actions » de la page réunion
    // (componentCrAnalysis), alimenté pendant l'analyse et non persisté.
    const customCellHtml = (a: MWAction): string =>
      `<td style="white-space:nowrap;padding:4px 6px;text-align:right;vertical-align:middle">
        ${window.componentCrAnalysis.crSourceBadgeHtml(a.id)}
        <button type="button" class="btn btn-sm btn-secondary mw-publish-action" data-aid="${a.id}" title="${esc(t('pageMeeting.generation.btnPublishTitle'))}" style="margin-right:4px">${esc(t('pageMeeting.generation.btnPublish'))}</button>
        <button type="button" class="btn btn-secondary btn-icon mw-edit-action" data-aid="${a.id}" title="${esc(t('common.edit'))}" style="color:#147A57;margin-right:4px">✎</button>
        <button type="button" class="btn btn-danger btn-icon mw-delete-action" data-aid="${a.id}" title="${esc(t('common.delete'))}">✕</button>
      </td>`;
    const rows = createdActions
      .map((a) => {
        const lite = {
          id: a.id,
          text: a.text,
          approver_id: a.approver_id,
          deadline: a.deadline,
          performers: a.performers,
        };
        const rowHtml = window.componentActionRow.buildActionRowHtml(lite as never, opts as never);
        return rowHtml.replace('</tr>', customCellHtml(a) + '</tr>');
      })
      .join('');

    return `
      ${subtitle}
      ${ctxLine}
      <div class="table-wrap">
        <table class="table">${headers}<tbody>${rows}</tbody></table>
      </div>
    `;
  }

  // Rafraîchit le body sans toucher au footer (la mise à jour du footer
  // remplacerait le bouton Fermer et perdrait son listener).
  function refreshStep2Body(): void {
    const body = document.getElementById('modal-body');
    if (body) body.innerHTML = renderStep2();
    wireStep2();
  }

  function wireStep2(): void {
    // Publier : appelle l'endpoint dédié qui flippe to_validate=false et
    // notifie. La ligne disparaît de la table — l'action est désormais une
    // action « normale », visible des destinataires.
    document.querySelectorAll<HTMLButtonElement>('.mw-publish-action').forEach((btn) => {
      btn.addEventListener('click', () => {
        const aid = parseInt(btn.dataset['aid'] || '0');
        if (!aid) return;
        void (async () => {
          try {
            await api.publishAction(aid);
            createdActions = createdActions.filter((a) => a.id !== aid);
            // La provenance ne sert qu'à arbitrer avant publication.
            window.componentCrAnalysis.forgetCrSource(aid);
            toast(t('pageMeeting.generation.toastPublished'), 'ok');
            refreshStep2Body();
          } catch (err: unknown) {
            toast(err instanceof Error ? err.message : t('common.error'), 'error');
          }
        })();
      });
    });

    // Supprimer : DELETE /actions/:id (refusé serveur si l'action n'est plus
    // un brouillon). Le serveur nettoie les liens projet/comité en cascade
    // (M2M action_projects / action_committees).
    document.querySelectorAll<HTMLButtonElement>('.mw-delete-action').forEach((btn) => {
      btn.addEventListener('click', () => {
        const aid = parseInt(btn.dataset['aid'] || '0');
        if (!aid) return;
        if (!confirm(t('pageMeeting.generation.confirmDelete'))) return;
        void (async () => {
          try {
            await api.deleteDraftAction(aid);
            createdActions = createdActions.filter((a) => a.id !== aid);
            window.componentCrAnalysis.forgetCrSource(aid);
            refreshStep2Body();
          } catch (err: unknown) {
            toast(err instanceof Error ? err.message : t('common.error'), 'error');
          }
        })();
      });
    });

    document.querySelectorAll<HTMLButtonElement>('.mw-edit-action').forEach((btn) => {
      btn.addEventListener('click', () => {
        const aid = parseInt(btn.dataset['aid'] || '0');
        if (!aid) return;
        // On laisse la modale d'édition d'action gérer l'aller-retour : à la
        // sauvegarde, on recharge l'action depuis le backend pour rafraîchir
        // les données affichées dans la table.
        void (async () => {
          let fresh: MWAction | undefined;
          try {
            // GET /actions/{id} : charge la seule action éditée (et pas toute
            // la table). L'endpoint renvoie aussi les drafts (to_validate),
            // donc pas besoin de includeDrafts ici.
            fresh = (await api.getAction(aid)) as MWAction;
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            toast(t('meetingWizard.toast.loadActionError', { msg }), 'error');
            return;
          }
          if (!fresh) {
            toast(t('meetingWizard.toast.actionNotFound'), 'error');
            return;
          }
          window.showEditActionModal(
            fresh as never,
            {
              users: users as never,
              statuses: statuses as never,
              projects: projects as never,
              onSaved: async () => {
                try {
                  // Seule l'action `aid` a changé : on ne recharge qu'elle.
                  const fresh2 = (await api.getAction(aid)) as MWAction;
                  createdActions = createdActions.map((a) => (a.id === aid ? fresh2 : a));
                } catch {
                  /* non bloquant */
                }
                refreshStep2Body();
              },
            } as never,
          );
        })();
      });
    });
  }

  // ── Cleanup au "Fermer" ─────────────────────────────────────────────────
  // Toutes les actions encore dans `createdActions` à la fermeture sont des
  // brouillons (to_validate=true) : non publiées, invisibles des autres
  // utilisateurs. On les supprime en bulk pour ne pas polluer la base. Les
  // erreurs individuelles sont silencieuses (warn) pour ne pas bloquer la
  // fermeture — le brouillon restera invisible de toute façon.
  async function cleanupRemainingDrafts(): Promise<void> {
    if (!createdActions.length) return;
    const ids = createdActions.map((a) => a.id).filter((id) => !!id);
    await Promise.all(
      ids.map(async (id) => {
        try {
          await api.deleteDraftAction(id);
        } catch (err: unknown) {
          safeWarn('[meeting-wizard] suppression brouillon KO', err);
        }
      }),
    );
    createdActions = [];
  }

  // ── Pilotage modale ──────────────────────────────────────────────────────
  function modalBody(): string {
    return step === 1 ? renderStep1() : renderStep2();
  }

  function modalTitle(): string {
    return t('meetingWizard.title', { n: step });
  }

  function refreshBody(): void {
    const body = document.getElementById('modal-body');
    if (body) body.innerHTML = modalBody();
    wireBody();
  }

  function refreshAll(): void {
    const title = document.getElementById('modal-title');
    if (title) title.textContent = modalTitle();
    refreshBody();
    updateFooter();
  }

  function wireBody(): void {
    if (step === 1) wireStep1();
    else wireStep2();
  }

  function updateFooter(): void {
    const confirmBtn = document.getElementById('modal-confirm') as HTMLButtonElement | null;
    const cancelBtn = document.getElementById('modal-cancel') as HTMLButtonElement | null;
    if (step === 1) {
      if (confirmBtn) {
        // Étape 1 → lance l'analyse IA (facturée) : label + picto « crédit IA ».
        confirmBtn.innerHTML = esc(t('meetingWizard.btn.step2')) + iaCreditBadge();
        confirmBtn.style.display = '';
      }
      if (cancelBtn) cancelBtn.style.display = '';
    } else {
      // Étape 2 : seul un bouton « Fermer » est nécessaire — l'utilisateur a
      // déjà ses actions créées, il n'y a rien à valider ni à compléter ici.
      // On masque le bouton primaire et on remplace le secondaire par
      // « Fermer ». On câble aussi le clic « Fermer » sur la fonction de
      // nettoyage : tout brouillon restant est supprimé avant fermeture.
      if (confirmBtn) confirmBtn.style.display = 'none';
      if (cancelBtn) {
        cancelBtn.textContent = t('common.close');
        cancelBtn.style.display = '';
        // Remplace le node pour purger le listener installé par openWideModal
        // (`closeModalSafe` direct) — sinon notre cleanup ne se déclenche pas.
        const fresh = cancelBtn.cloneNode(true) as HTMLButtonElement;
        cancelBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => {
          void (async () => {
            fresh.disabled = true;
            try {
              await cleanupRemainingDrafts();
            } finally {
              closeModal();
            }
          })();
        });
      }
    }
  }

  async function onConfirm(): Promise<void> {
    if (step === 1) return gotoStep2();
    // Au step 2, le bouton confirm est masqué par updateFooter. Ce code
    // n'est appelé que par la touche Entrée si l'utilisateur l'utilise pour
    // valider. On déclenche dans tous les cas le nettoyage avant fermeture.
    await cleanupRemainingDrafts();
    closeModal();
  }

  openWideModal(modalTitle(), modalBody(), onConfirm, t('meetingWizard.btn.step2'));
  setTimeout(() => {
    wireBody();
    updateFooter();
    // Filet de sécurité : tous les chemins de fermeture (Esc, clic overlay,
    // ✕ en haut à droite, bouton « Fermer ») retirent le node #modal-overlay
    // du DOM. On observe sa parentèle et on déclenche cleanupRemainingDrafts
    // dès que le node disparaît. Le cleanup explicite du bouton « Fermer »
    // est conservé pour rester fluide (vidage avant l'animation de close),
    // mais l'observer assure qu'aucun brouillon n'orpheline si l'utilisateur
    // ferme autrement.
    const overlay = document.getElementById('modal-overlay');
    const parent = overlay?.parentNode;
    if (overlay && parent) {
      const observer = new MutationObserver((muts) => {
        for (const mut of muts) {
          for (const removed of Array.from(mut.removedNodes)) {
            if (removed === overlay) {
              observer.disconnect();
              void cleanupRemainingDrafts();
              return;
            }
          }
        }
      });
      observer.observe(parent, { childList: true });
    }
  }, 0);
};

export {};
